import { Logo, PageLoader, ProgressBar } from "@components";
import { usePostHogIdentify, useFetch } from "@hooks";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { ArrowLeftShort } from "react-bootstrap-icons";
import { Outlet, useMatches, useNavigate, useParams } from "react-router-dom";
import { isEmbeddedInStore, notifyStoreParent } from "@utils/embeddedStore";
import { getSoleCheckoutMethodRoute } from "./hooks/flowRouting";
import { getInstitutions } from "../../api/institutionsController";
import {
  useSummaryData,
  useSelectedInvoiceIds,
  useInvoicePaymentAmount,
  useInvoiceToggle,
  useFlowRouting,
} from "./hooks";
import { useEarlySettlementDiscount } from "@flags";

const PaymentFlow = ({ flowId, getDataFunction }) => {
  const { id } = useParams();
  const matches = useMatches();
  const navigate = useNavigate();
  const routeId = matches[matches.length - 1].id;

  const [paymentParameters, setPaymentParameters] = useState(null);
  const [webhookPrResponse, setWebhookPrResponse] = useState(null);
  const [methodStepData, setMethodStepData] = useState(null);

  const [selectedInvoiceIds, setSelectedInvoiceIds] = useSelectedInvoiceIds(
    paymentParameters?.statement
  );
  const [
    invoicePaymentAmount,
    transactionIds,
    setInvoicePaymentAmount,
    setTransactionIds,
  ] = useInvoicePaymentAmount(selectedInvoiceIds, paymentParameters?.statement);

  const effectiveAmount =
    paymentParameters &&
    (typeof invoicePaymentAmount === "number"
      ? invoicePaymentAmount
      : paymentParameters.amountRequested);

  const { toggleInvoice, toggleGroup } = useInvoiceToggle(
    paymentParameters?.statement,
    selectedInvoiceIds,
    setSelectedInvoiceIds
  );

  const {
    data: allInstitutions,
    isLoading: institutionsLoading,
    error: institutionsError,
  } = useFetch(getInstitutions, ["institutions"]);

  const scrollContainerRef = useRef(null);

  const setSelectedInvoices = async (amountToPay, ids) => {
    setTransactionIds(ids);
    setInvoicePaymentAmount(amountToPay);
  };

  const summaryData = useSummaryData(paymentParameters, transactionIds);

  const earlySettlementEnabled = useEarlySettlementDiscount();
  const settlementDiscount = useMemo(() => {
    if (!earlySettlementEnabled || !paymentParameters?.statement?.invoiceGroups || !transactionIds) return 0;
    const selectedSet = new Set(transactionIds);
    const groups = paymentParameters.statement.invoiceGroups;
    const invoiceDiscount = groups
      .flatMap((g) => g.invoices)
      .filter((inv) => selectedSet.has(inv.ledgerTransactionId) && inv.discountAmount > 0)
      .reduce((sum, inv) => sum + inv.discountAmount, 0);
    const creditNoteDiscount = groups
      .flatMap((g) => [...(g.invoices.flatMap((inv) => inv.creditNotes || [])), ...(g.creditNotes || [])])
      .filter((cn) => selectedSet.has(cn.ledgerTransactionId) && (cn.applcableDisocunt ?? 0) !== 0)
      .reduce((sum, cn) => sum + (cn.applcableDisocunt ?? 0), 0);
    return Math.round((invoiceDiscount + creditNoteDiscount) * 100) / 100;
  }, [earlySettlementEnabled, paymentParameters, transactionIds]);

  const paymentDiscounts = useMemo(() => {
    if (!paymentParameters?.statement?.invoiceGroups || !transactionIds) return null;
    const selectedSet = new Set(transactionIds);
    const groups = paymentParameters.statement.invoiceGroups;
    const invoiceDiscounts = groups
      .flatMap((g) => g.invoices)
      .filter((inv) => selectedSet.has(inv.ledgerTransactionId) && (inv.discountAmount ?? 0) > 0)
      .map((inv) => ({ ledgerTransactionId: inv.ledgerTransactionId, amount: inv.discountAmount, discountType: 'EarlySettlement' }));
    const creditNoteDiscounts = groups
      .flatMap((g) => [...(g.invoices.flatMap((inv) => inv.creditNotes || [])), ...(g.creditNotes || [])])
      .filter((cn) => selectedSet.has(cn.ledgerTransactionId) && (cn.applcableDisocunt ?? 0) !== 0)
      .map((cn) => ({ ledgerTransactionId: cn.ledgerTransactionId, amount: cn.applcableDisocunt, discountType: 'EarlySettlement' }));
    const all = [...invoiceDiscounts, ...creditNoteDiscounts];
    return all.length > 0 ? all : null;
  }, [paymentParameters, transactionIds]);

  usePostHogIdentify(
    {
      customerId: paymentParameters?.customerId || paymentParameters?.id,
      customerName: paymentParameters?.customerName,
      merchantId: paymentParameters?.merchant?.id,
      merchantName: paymentParameters?.merchant?.name,
      organisationId: paymentParameters?.merchant?.organisationId,
    },
    !!paymentParameters
  );

  const progressMap = {
    [`${flowId}-base`]: "20%",
    [`${flowId}-selectinvoices`]: "20%",
    [`${flowId}-paymentmethod`]: "40%",
    [`${flowId}-paybybank`]: "60%",
    [`${flowId}-paybycard`]: "60%",
    [`${flowId}-paybybnpl`]: "60%",
    [`${flowId}-confirmpaybybank`]: "70%",
    [`${flowId}-processing`]: "80%",
    [`${flowId}-yapilycallback`]: "80%",
    [`${flowId}-success`]: "full",
    [`${flowId}-cancelled`]: "full",
  };

  const backButtonWhitelist = [
    `${flowId}-paymentmethod`,
    `${flowId}-confirmpaybybank`,
    `${flowId}-paybycard`,
    `${flowId}-paybybank`,
    `${flowId}-paybybnpl`,
  ];

  // A store checkout runs inside the merchant's page, usually in an iframe
  // whose history is shared with the store. history.back() from the first
  // hosted step therefore leaves the payment flow for the store's previous
  // checkout step. Store checkouts navigate explicitly instead, and show no
  // back arrow where the flow has no earlier step of its own; leaving the
  // payment is the store's own cancel link.
  const goBack = (() => {
    if (!backButtonWhitelist.includes(routeId)) return null;
    if (flowId !== "checkout") return () => navigate(-1);

    const step = routeId.slice(flowId.length + 1);
    if (step === "paymentmethod") return null;
    if (step === "confirmpaybybank") {
      return () => navigate(`/${flowId}/${id}/paybybank`);
    }

    const soleRoute = getSoleCheckoutMethodRoute({
      flowId,
      options: [
        { route: "paybybank", valid: !!paymentParameters?.openBankingPaymentAllowed },
        { route: "paybycard", valid: !!paymentParameters?.cardPaymentAllowed },
        { route: "paybybnpl", valid: !!paymentParameters?.klarnaPaymentAllowed },
      ],
    });
    if (soleRoute) return null;

    return () => navigate(`/${flowId}/${id}/paymentmethod`);
  })();

  // Embedded in a store, the hosted page sizes its own iframe: the store
  // listens for ontik:express:resize (the WooCommerce express component
  // speaks the same message). Reported only while the content overflows,
  // and only ever upwards, so a growing frame cannot feed back into itself.
  const headerRef = useRef(null);
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !isEmbeddedInStore()) return undefined;

    let frame = 0;
    const report = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const doc = document.documentElement;
        const header = headerRef.current ? headerRef.current.offsetHeight : 0;
        const documentOverflows = doc.scrollHeight > window.innerHeight + 1;
        const containerOverflows = container.scrollHeight > container.clientHeight + 1;
        if (!documentOverflows && !containerOverflows) return;

        notifyStoreParent({
          type: "ontik:express:resize",
          height: Math.max(doc.scrollHeight, header + container.scrollHeight) + 2,
        });
      });
    };
    const sizes = new ResizeObserver(report);
    const observeChildren = () =>
      Array.from(container.children).forEach((child) => sizes.observe(child));

    sizes.observe(document.body);
    sizes.observe(container);
    observeChildren();
    const mutations = new MutationObserver(() => {
      observeChildren();
      report();
    });
    mutations.observe(container, { childList: true, subtree: true });
    report();

    return () => {
      cancelAnimationFrame(frame);
      sizes.disconnect();
      mutations.disconnect();
    };
  }, [routeId, paymentParameters]);

  const onBankPaymentConfirm = (institution) => {
    setMethodStepData({ method: "paybybank", institution });
    navigate(`/${flowId}/${id}/confirmpaybybank`);
  };

  const successUrl =
    paymentParameters?.successUrl || `/${flowId}/${id}/success`;

  useFlowRouting({
    flowId,
    id,
    routeId,
    paymentParameters,
    methodStepData,
    navigate,
  });

  // Scroll to top on route change. The window scrolls on mobile (natural
  // document flow); the inner container scrolls on desktop (two-column layout).
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: "auto" });
    // Inside a store's frame it is the store's page that may be scrolled
    // past the top of the frame, after a taller previous step; only the
    // store can bring the new step's top back into view.
    notifyStoreParent({ type: "ontik:express:step_changed", step: routeId });
  }, [routeId]);

  // Fetch unified flow data (PaymentFlowPublicDto)
  const refreshPaymentRequest = useCallback(async () => {
    if (!id) return null;
    const latest = await getDataFunction(id);
    setPaymentParameters(latest);
    return latest;
  }, [id, getDataFunction]);

  useEffect(() => {
    refreshPaymentRequest();
  }, [refreshPaymentRequest]);

  return paymentParameters ? (
    <div className="flex flex-col w-full min-h-dvh lg:fixed lg:top-0 lg:left-0 lg:h-dvh">
      <div ref={headerRef} className="sticky top-0 z-10">
        {goBack && (
          <button
            className="absolute left-5 top-5 text-zinc-500 size-8 hover:bg-zinc-50 rounded-full cursor-pointer"
            role="button"
            onClick={goBack}
          >
            <ArrowLeftShort className="size-8" />
          </button>
        )}
        <div className="bg-white flex items-center justify-center py-2 h-[68px]">
          <Logo merchant={paymentParameters.merchant} />
        </div>
        <ProgressBar
          merchant={paymentParameters.merchant}
          width={progressMap[routeId]}
        />
      </div>
      <div
        ref={scrollContainerRef}
        className="flex-1 flex flex-col lg:overflow-auto"
      >
        <Outlet
          context={{
            id,
            flowId,
            // spread all payment-related fields directly into context
            ...paymentParameters,
            cardPaymentProvider:
              paymentParameters?.merchant?.cardPaymentProvider,
            amount: effectiveAmount,
            transactionIds,
            setSelectedInvoices,
            setMethodStepData,
            methodStepData,
            webhookPrResponse,
            setWebhookPrResponse,
            refreshPaymentRequest,
            allInstitutions,
            institutionsLoading,
            institutionsError,
            onBankPaymentConfirm,
            processingUrl: `/${flowId}/${id}/processing`,
            successUrl,
            summaryData,
            settlementDiscount,
            paymentDiscounts,
            selectedInvoiceIds,
            toggleInvoice,
            toggleGroup,
          }}
        />
      </div>
    </div>
  ) : (
    <PageLoader />
  );
};

export default PaymentFlow;
